import { useState, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import { Plus, ChevronLeft, Building2 } from "lucide-react";
import LinknetForm, { TenantInfo, PicData } from "./components/linknet/LinknetForm";
import { GetTenantMall, SaveVendorSync, GetVendorSyncDatas, GetRegistrationHistory, VendorSyncHistoryData, GetBrandDev } from "../../services/linknet";
import { cx, sortData } from "../../utils";
import Dropdown from "../../components/dropdown/Index";
import useMallTenantDropdown from "../../hooks/useMallTenantDropdown";

const getMallAddress = (mallName: string): string => {
  const name = mallName.toLowerCase();
  if (name.includes("puri")) {
    return "Puri Indah CBD, Jl. Puri Indah Raya, RT.3/RW.2, Kembangan Sel., Kec. Kembangan, Kota Jakarta Barat, Daerah Khusus Ibukota Jakarta 11610";
  }
  if (name.includes("kemang")) {
    return "Jl. Kemang Raya No. 36, RT.12/RW.5, Bangka, Kec. Mampang Prpt., Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12730";
  }
  return `${mallName}, Jakarta, Indonesia`;
};

declare const API_BASE_URL_TOKEN_DEV: string;

type SortKey = "TenantNewId" | "TenantName" | "Unit" | "PICName" | "PICPhone" | "PICEmail" | "CreatedOn" | "Notes";

const INITIAL_TENANT: TenantInfo = {
  tenantName: "",
  mallName: "",
  unit: "",
  alamat: "",
};

const INITIAL_PIC: PicData = {
  picName: "",
  picPhone: "",
  picEmail: "",
  notes: "",
};

export default function LinknetPage() {
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>(INITIAL_TENANT);
  const [pic, setPic] = useState<PicData>(INITIAL_PIC);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [historyData, setHistoryData] = useState<VendorSyncHistoryData[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("CreatedOn");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedHistory = useMemo(() => {
    return sortData<VendorSyncHistoryData>(historyData, sortKey, sortDir);
  }, [historyData, sortKey, sortDir]);

  const fetchHistory = async (tenantId: number) => {
    setHistoryLoading(true);
    try {
      if (tenantId > 0) {
        const res = await GetRegistrationHistory(tenantId);
        if (res && res.VendorSyncHistoryDatas && res.VendorSyncHistoryDatas.length > 0) {
          setHistoryData(res.VendorSyncHistoryDatas);
          setShowForm(false);
        } else {
          setHistoryData([]);
          setShowForm(true);
        }
      } else {
        setHistoryData([]);
        setShowForm(true);
      }
    } catch (error) {
      console.log("Error fetching registration history:", error);
      setHistoryData([]);
      setShowForm(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchData = async (tenantId: number, mallId?: number) => {
    setLoading(true);
    try {
      const data = await GetTenantMall(tenantId, 0, 0);
      const d = data?.tenantMallDatas?.[0];
      if (d) {
        setTenantInfo({
          tenantName: d.BrandName,
          mallName: d.MallName,
          unit: d.Unit,
          alamat: d.Address,
        });
        setPic({
          picName: d.PICName || "",
          picPhone: d.PICPhone || "",
          picEmail: d.PICEmail || "",
          notes: "",
        });
      } else {
        // Fallback for HO users selecting outside their default Mall
        const brandData = await GetBrandDev(tenantId);
        const b = brandData?.brandDetailDatas?.[0];
        if (b) {
          const mId = mallId ?? selectedMall;
          const mallName = b.MallName || mallOptions.find((o) => o.value === mId)?.label || "";
          const tenantName = b.BrandName || tenantOptions.find((o) => o.value === tenantId)?.label || "";
          setTenantInfo({
            tenantName,
            mallName,
            unit: b.Unit || "",
            alamat: getMallAddress(mallName),
          });
          setPic({
            picName: "",
            picPhone: "",
            picEmail: "",
            notes: "",
          });
        } else {
          // Last resort fallback using selected dropdown labels directly
          const mId = mallId ?? selectedMall;
          const mallName = mallOptions.find((o) => o.value === mId)?.label || "";
          const tenantName = tenantOptions.find((o) => o.value === tenantId)?.label || "";
          if (tenantName) {
            setTenantInfo({
              tenantName,
              mallName,
              unit: "",
              alamat: getMallAddress(mallName),
            });
          } else {
            setTenantInfo(INITIAL_TENANT);
          }
          setPic(INITIAL_PIC);
        }
      }
    } catch (error) {
      console.log("Error fetching tenant info:", error);
      setTenantInfo(INITIAL_TENANT);
      setPic(INITIAL_PIC);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = useCallback((tenantId: number, mallId?: number) => {
    if (tenantId > 0) {
      fetchHistory(tenantId);
      fetchData(tenantId, mallId);
    } else {
      setHistoryData([]);
      setShowForm(true);
      setTenantInfo(INITIAL_TENANT);
      setPic(INITIAL_PIC);
      setHistoryLoading(false);
      setLoading(false);
    }
  }, []);

  const {
    mallOptions,
    tenantOptions,
    selectedMall,
    selectedTenant,
    setSelectedMall,
    setSelectedTenant,
  } = useMallTenantDropdown(handleTenantChange);

  const handlePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPic((prev) => ({ ...prev, [name]: value }));
  };

  const handleDiscard = () => {
    setPic(INITIAL_PIC);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const checkData = await GetVendorSyncDatas();
      let proceed = true;

      if (
        checkData &&
        checkData.VendorSyncDatas &&
        checkData.VendorSyncDatas.length > 0 &&
        checkData.TotalRecord !== 0
      ) {
        const confirmResult = await Swal.fire({
          title: "Konfirmasi",
          text: "Tenant sudah terdaftar, apakah anda ingin mendaftarkan ulang?",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#4d5bd5",
          cancelButtonColor: "#aaa",
          confirmButtonText: "Ya",
          cancelButtonText: "Batal",
        });
        if (!confirmResult.isConfirmed) {
          proceed = false;
        }
      }

      if (proceed) {
        const result = await SaveVendorSync({
          PICName: pic.picName,
          PICPhone: pic.picPhone,
          PICEmail: pic.picEmail,
          Address: tenantInfo.alamat,
          TenantName: tenantInfo.tenantName,
          Unit: tenantInfo.unit,
          Notes: pic.notes,
        });

        if (result && result.Code === 0) {
          console.log("Vendor sync saved successfully:", result.Message);
          Swal.fire({
            icon: "success",
            title: result.Message,
            text: "Data berhasil disimpan.",
            confirmButtonColor: "#4d5bd5",
          }).then(() => {
            const tenantNewId = result.TenantNewId || "";
            if (selectedTenant) {
              fetchHistory(selectedTenant);
            }
            setShowForm(false);
            window.location.href = `${API_BASE_URL_TOKEN_DEV}?tenantNewId=${tenantNewId}`;
          });
        } else {
          console.log("Failed to save vendor sync:", result?.Message);
          Swal.fire({
            icon: "error",
            title: "Gagal",
            text: result?.Message || "Terjadi kesalahan saat menyimpan data.",
            confirmButtonColor: "#4d5bd5",
          });
        }
      }
    } catch (error) {
      console.log("Error saving vendor sync:", error);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="p-3 sm:p-4 max-w-5xl mx-auto">
        <div className="flex flex-row items-center justify-between gap-3 mb-4">
          <div className="shrink-0">
            {!showForm && selectedTenant && selectedTenant > 0 && (
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-all shadow-sm whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                New Request
              </button>
            )}
          </div>

          <div className="flex items-center justify-end sm:min-w-[320px] sm:min-h-[36px]">
            <Dropdown
              mallSelected={(val) => setSelectedMall(val)}
              mallOptions={mallOptions}
              tenantSelected={(val) => setSelectedTenant(val)}
              tenantOptions={tenantOptions}
              mallValue={selectedMall}
              tenantValue={selectedTenant}
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          {!selectedTenant || selectedTenant === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                <Building2 className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Pilih Tenant Terlebih Dahulu
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                Silakan pilih lokasi Mall dan Tenant pada menu dropdown di atas untuk melihat histori registrasi atau membuat request baru.
              </p>
            </div>
          ) : showForm ? (
            loading ? (
              <div className="flex items-center justify-center h-40 text-gray-400 dark:text-gray-500 text-sm">
                Loading...
              </div>
            ) : (
              <div>
                {/* Back button above form, only rendered if history is not empty */}
                {historyData.length > 0 && (
                  <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700">
                    <button
                      onClick={() => setShowForm(false)}
                      className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Kembali
                    </button>
                  </div>
                )}
                <LinknetForm
                  tenantInfo={tenantInfo}
                  pic={pic}
                  onPicChange={handlePicChange}
                  onDiscard={handleDiscard}
                  onSave={handleSave}
                  saving={saving}
                />
              </div>
            )
          ) : (
            <div>

              {/* Mobile View */}
              <div className="block sm:hidden p-4">
                {historyLoading ? (
                  <div className="flex items-center justify-center h-40 text-gray-400 dark:text-gray-500 text-sm">
                    Loading histori...
                  </div>
                ) : sortedHistory.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm">
                    Tidak ada histori registrasi.
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {sortedHistory.map((row) => (
                      <div
                        key={row.TenantRegistrationId}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex flex-col gap-2 shadow-sm"
                      >
                        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-2">
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                            Request ID: {row.TenantNewId || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1.5 pt-1">
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">Tenant Name</span>
                            <span className="text-gray-800 dark:text-gray-100 font-medium">{row.TenantName || "-"}</span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">Unit</span>
                            <span className="text-gray-800 dark:text-gray-100 font-medium">{row.Unit || "-"}</span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">PIC Name</span>
                            <span className="text-gray-800 dark:text-gray-100">{row.PICName || "-"}</span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">PIC Phone</span>
                            <span className="text-gray-800 dark:text-gray-100">{row.PICPhone || "-"}</span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">PIC Email</span>
                            <span className="text-gray-800 dark:text-gray-100 break-all">{row.PICEmail || "-"}</span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">Date</span>
                            <span className="text-gray-500 dark:text-gray-400">{formatDate(row.CreatedOn)}</span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-24 shrink-0">Notes</span>
                            <span className="text-gray-800 dark:text-gray-100">{row.Notes || ""}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Desktop View */}
              <div className="hidden sm:block overflow-x-auto">
                {historyLoading ? (
                  <div className="flex items-center justify-center h-40 text-gray-400 dark:text-gray-500 text-sm">
                    Loading histori...
                  </div>
                ) : sortedHistory.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm">
                    Tidak ada histori registrasi.
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="TenantNewId" onClick={toggleSort}>Request ID</Th>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="TenantName" onClick={toggleSort}>Tenant</Th>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="Unit" onClick={toggleSort}>Unit</Th>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="PICName" onClick={toggleSort}>PIC Name</Th>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="PICPhone" onClick={toggleSort}>PIC Phone</Th>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="PICEmail" onClick={toggleSort}>PIC Email</Th>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="CreatedOn" onClick={toggleSort}>Tanggal Registrasi</Th>
                        <Th sortable activeKey={sortKey} dir={sortDir} k="Notes" onClick={toggleSort}>Notes</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {sortedHistory.map((row) => (
                        <tr
                          key={row.TenantRegistrationId}
                          className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                        >
                          <td className="py-4 px-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {row.TenantNewId || "-"}
                          </td>
                          <td className="py-4 px-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {row.TenantName || "-"}
                            </div>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row.Unit || "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row.PICName || "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row.PICPhone || "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row.PICEmail || "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {formatDate(row.CreatedOn)}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row.Notes || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Th({
  children,
  sortable,
  k,
  activeKey,
  dir,
  onClick,
}: {
  children: React.ReactNode;
  sortable?: boolean;
  k?: SortKey;
  activeKey?: SortKey;
  dir?: "asc" | "desc";
  onClick?: (k: SortKey) => void;
}) {
  const isActive = sortable && k === activeKey;
  return (
    <th
      className={cx(
        "py-3 px-4 text-left select-none whitespace-nowrap",
        sortable && "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800",
      )}
      onClick={sortable && k && onClick ? () => onClick(k as SortKey) : undefined}
    >
      <div className="inline-flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{children}</span>
        {sortable && (
          <svg
            className={cx("w-3 h-3", isActive ? "text-gray-600 dark:text-gray-300" : "text-gray-400")}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            {isActive ? (
              dir === "asc" ? <path d="M5 15l7-7 7 7" /> : <path d="M19 9l-7 7-7-7" />
            ) : (
              <>
                <path d="M5 15l7-7 7 7" />
                <path d="M5 9l7 7 7-7" opacity="0.4" />
              </>
            )}
          </svg>
        )}
      </div>
    </th>
  );
}
